import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { PassportDetailsStore } from '../shared/passport-details.store';

export type PassportDetailsMode = 'view';

export interface PersonDetails {
  passportID?: string;
  origin?: string;
  destination?: string;
  destinationWeighbridge?: string;
  apn?: string;
  apnName?: string;
  vendorName?: string;
  vendorNumber?: string;
  haulier?: string;
}

@Component({
  selector: 'app-passport-details',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './passport-details.component.html',
  styleUrl: './passport-details.component.css'
})
export class PassportDetailsComponent {
  @Input() person: PersonDetails | null = null;
  @Input() mode: PassportDetailsMode = 'view';
  @Input() primaryLabel: 'Add' | 'View' = 'Add';
  @Input() hideAdd: boolean = false;
  @Input() hidePassportComplete: boolean = false;
  @Output() add = new EventEmitter<void>();
  @Output() history = new EventEmitter<void>();
  @Output() comments = new EventEmitter<void>();

  editing = signal<PersonDetails | null>(null);

  constructor(private readonly http: HttpClient, private readonly store: PassportDetailsStore) {}

  onFieldChange<K extends keyof PersonDetails>(key: K, value: string): void {
    const current = this.editing();
    if (!current) return;
    this.editing.set({ ...current, [key]: value });
  }

  async fetchComments(): Promise<void> {
    const userID = localStorage.getItem('userID');
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    // Use correct passport ID property
    const passportId = (this.person as any)?.passportID ;
    if (!token || !userID || !passportId) {
      console.warn('Missing token, user ID, or passport ID');
      return;
    }
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/comments`;
    const headers = {
      'Authorization': `${tokenType} ${token}`,
      'Content-Type': 'application/json'
    };
    const body = {
      PassportId: Number.parseInt(String(passportId), 10),
      UserId: Number.parseInt(userID, 10)
    };
    try {
      const response = await firstValueFrom(
        this.http.post(apiUrl, body, { headers, withCredentials: true })
      );
      console.log('Comments response:', response);
    } catch (error) {
      console.warn('Failed to fetch comments:', error);
    }
  }

  get passportComplete(): 'Yes' | 'No' {
    const id = (this.person?.passportID || '').toString();
    if (!id) return 'No';
    const s = this.store.get(id);
    if (!s) return 'No';
    // Use backend isPassportCompleted field if available
    if (s.isPassportCompleted !== undefined) {
      return s.isPassportCompleted ? 'Yes' : 'No';
    }
    // Fallback to legacy calculation for backward compatibility
    const hasOrigin = !!(s.origin?.trim());
    const hasLoading = !!(s.loadingDateTime && s.loadingDateTime.length >= 16);
    const hasBulker = !!(s.bulkerNumber?.trim());
    const hasAuthorised = !!(s.authorisedBy?.trim());
    const imagesLen = (s.haccpImages || []).length;
    const haccpOk = (s.haccpCheck === 'No') || (s.haccpCheck === 'Yes' && imagesLen > 0);
    return (hasOrigin && hasLoading && hasBulker && hasAuthorised && haccpOk) ? 'Yes' : 'No';
  }

  get formattedApn(): string {
    if (!this.person) return '';
    
    return this.person.apn || '';
  }

}